import type { FleetTabKey } from './claude-fleet.overview'

/** The Claude Fleet page's tabs, in the order the tab bar shows them; a hash opens only a tab listed here. */
export const FLEET_TABS: { label: string; value: FleetTabKey }[] = [
  { label: 'Overview', value: 'overview' },
  { label: 'Runs', value: 'runs' },
  { label: 'Loops', value: 'loops' },
  { label: 'Runners', value: 'runners' },
  { label: 'Costs', value: 'costs' },
  { label: 'Settings', value: 'settings' },
  { label: 'Audit', value: 'audit' }
]

/** Tabs that became pages of their own: a link to `#release` or `#pipelines` opens the page, by route name. */
export const FLEET_MOVED_TABS: Partial<Record<FleetTabKey, string>> = { release: 'release-view', pipelines: 'claude-pipelines' }

/** The tab a hash names, before any card or query: `#RUNS/x?y` is `runs`, and an empty hash is the Overview. */
export function fleetHashTab(hash: string): FleetTabKey {
  return (hash.replace(/^#/, '').split(/[/?]/)[0]!.toLowerCase() || 'overview') as FleetTabKey
}

/** A tab's label, as the sidebar and the page title name it. */
export const fleetTabLabel = (tab: FleetTabKey) => FLEET_TABS.find(item => item.value === tab)?.label

/** The page a hash names when its tab moved out of the Fleet page, or undefined. */
export function movedFleetTab(hash: string): string | undefined {
  return FLEET_MOVED_TABS[fleetHashTab(hash)]
}

export const FLEET_TAB_KEYS: FleetTabKey[] = FLEET_TABS.map(item => item.value)

/**
 * The tab a URL hash opens, and the hash written the way the page writes it: `#RUNS` and
 * `#runs?mine=1` open Runs as `#runs`, and `#settings/lpe-loop` keeps its card. Null for a hash that
 * names no tab; an empty hash is the Overview.
 */
export function normalizeFleetHash(hash: string): { tab: FleetTabKey; hash: string } | null {
  const body = hash.replace(/^#/, '').split('?')[0]!
  if (!body) return { tab: 'overview', hash: '' }
  const tab = fleetHashTab(body)
  if (!FLEET_TAB_KEYS.includes(tab)) return null
  const rest = body.split('/').slice(1)
  const card = rest.join('/')
  return { tab, hash: card ? `#${tab}/${card}` : `#${tab}` }
}

/** How often the page re-reads its live sections while it is in front. */
export const FLEET_PAGE_REFRESH_MS = 30_000

/** "Updated just now", "Updated 40s ago", "Updated 3m ago": how fresh what the page shows is. */
export function updatedAgoText(ageMs: number): string {
  const seconds = Math.max(0, Math.round(ageMs / 1000))
  if (seconds < 10) return 'Updated just now'
  if (seconds < 60) return `Updated ${Math.floor(seconds / 10) * 10}s ago`
  return `Updated ${Math.floor(seconds / 60)}m ago`
}
